import React, { useCallback, useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import LanguageDropdown from "./LanguageDropdown";
import LogoutButton from "./LogoutButton";
import { useTranslation } from "react-i18next";
import logo_with_bg from "../../images/logo_with_bg.png";
import { BrightMode, DarkMode, ProfileIcon } from "./ProjectIcons";
import { useProfile } from "./ProfileProvider";
import { ProjectRoles } from "./Enums";
import tailwindClassnames from "./TailwindClassnames";
import { openReportProblem } from "./reportProblemBus";
import { USER_GUIDE_URL } from "../../constants";

function useRouteMatch(paths: string[]): boolean {
  const { pathname } = useLocation();
  return paths.some((path) => pathname.startsWith(path));
}

const Header: React.FC = () => {
  const { t } = useTranslation();
  // The Admin tab points at "/", which NavLink's isActive only matches exactly,
  // so every deeper admin route has to be listed here or the tab goes
  // unhighlighted — project and organization details, the add forms and the
  // user pages all live in the admin section. Prefixes, so nested subpages
  // (tabs, deployment ids) are covered too.
  const matchAdminSection = useRouteMatch([
    "/projects",
    "/addProject",
    "/organizations",
    "/addOrganization",
    "/users",
    "/sites",
  ]);
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [isOwnerEditorContributor, setIsOwnerEditorContributor] =
    useState(false);
  const { theme, toggleTheme, profile, profileLoading } = useProfile(); // Use theme and toggleTheme from context
  const isDarkMode = theme === "dark";
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const profileRef = useRef<HTMLButtonElement | null>(null);
  const getNavLinkClass = (
    isActive: boolean,
    isActiveCondition: boolean = false,
  ) =>
    isActive || isActiveCondition
      ? tailwindClassnames.navPillActive
      : tailwindClassnames.navPill;
  const toggleDropdown = (visibility: boolean) =>
    setIsDropdownVisible(visibility);

  useEffect(() => {
    if (isDropdownVisible) {
      const handleClickOutside = (event: any) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          profileRef.current &&
          !profileRef.current.contains(event.target)
        ) {
          toggleDropdown(false);
        }
      };

      // Add the event listener
      document.addEventListener("mousedown", handleClickOutside);

      // Cleanup function to remove the event listener
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [isDropdownVisible, dropdownRef]);

  /**
   * Check if the user has permissions to view media management or AI processing page based on their role.
   *
   * Declared above the effect that consumes it: a dependency array is evaluated
   * during render, so naming this const while it was still defined below would
   * hit the temporal dead zone.
   */
  const checkViewerPermissions = useCallback((): boolean => {
    if (profile) {
      const isAdmin = profile?.is_admin;
      const isProjectEditorOrContributor = profile.project_roles.some(
        (role: any) =>
          role.role === ProjectRoles.OWNER ||
          role.role === ProjectRoles.EDITOR ||
          role.role === ProjectRoles.CONTRIBUTOR,
      );
      return isAdmin || isProjectEditorOrContributor;
    }
    return false;
  }, [profile]);

  useEffect(() => {
    if (!profileLoading && profile) {
      setIsOwnerEditorContributor(checkViewerPermissions());
    }
  }, [profileLoading, profile, checkViewerPermissions]);

  return profileLoading && !profile ? (
    <div className="h-16 px-4 py-2 bg-primary-off-white dark:bg-storm-blue-dark w-full border-b border-off-white-dark dark:border-storm-blue-border">
      <div className="h-full flex items-center">
        <div className="text-blue-600 text-xl font-semibold mr-8">
          <img
            src={logo_with_bg}
            alt="akili logo"
            width={341}
            height={338}
            className="h-10 w-auto rounded-full bg-sky-blue shadow shadow-xs shadow-sky-blue"
          />
        </div>
      </div>
    </div>
  ) : (
    <header className="z-20 bg-primary-off-white dark:bg-storm-blue-dark w-full transition duration-300">
      <nav className="h-16 px-4 py-2 border-b border-off-white-dark dark:border-storm-blue-border transition duration-300">
        <div className="h-full flex justify-between items-center">
          <div className="flex items-center">
            <div className="text-blue-600 text-xl font-semibold mr-8">
              <img
                src={logo_with_bg}
                alt="akili logo"
                width={341}
                height={338}
                className="h-10 w-auto rounded-full bg-sky-blue shadow shadow-xs shadow-sky-blue"
              />
            </div>
          </div>
          <div className="flex flex-row">
            <div className="flex items-baseline space-x-4">
              <NavLink
                to="/"
                className={({ isActive }) =>
                  getNavLinkClass(isActive, matchAdminSection)
                }
                onClick={() => toggleDropdown(false)}
              >
                {t("Admin")}
              </NavLink>

              {isOwnerEditorContributor && (
                <NavLink
                  to="/manage"
                  className={({ isActive }) => getNavLinkClass(isActive)}
                  onClick={() => toggleDropdown(false)}
                >
                  {t("Media Management")}
                </NavLink>
              )}

              {isOwnerEditorContributor && (
                <NavLink
                  to="/ai-processing"
                  className={({ isActive }) => getNavLinkClass(isActive)}
                  onClick={() => toggleDropdown(false)}
                >
                  {t("AI Processing")}
                </NavLink>
              )}

              <NavLink
                to="/review"
                className={({ isActive }) => getNavLinkClass(isActive)}
                onClick={() => toggleDropdown(false)}
              >
                {t("Review")}
              </NavLink>

              <button
                type="button"
                onClick={toggleTheme}
                aria-label={
                  isDarkMode
                    ? t("Switch to light mode")
                    : t("Switch to dark mode")
                }
                className="mx-3 rounded-lg p-2 transition duration-150 hover:bg-black/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue dark:hover:bg-white/10"
              >
                {isDarkMode ? <DarkMode /> : <BrightMode />}
              </button>
            </div>
            {/* Real button (was a click-handler div) so the account menu is
                keyboard-operable. It is a disclosure, not an ARIA menu: the popup
                below is a plain div, so only aria-expanded is claimed here. */}
            <button
              type="button"
              ref={profileRef}
              onClick={() => toggleDropdown(!isDropdownVisible)}
              aria-label={t("Account menu")}
              aria-expanded={isDropdownVisible}
              className="ml-4 flex items-center rounded-full transition duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-storm-blue dark:focus-visible:ring-sky-blue"
            >
              <ProfileIcon />
            </button>
            {isDropdownVisible && (
              <div ref={dropdownRef} className={tailwindClassnames.menuCard}>
                <NavLink
                  to="/myaccount"
                  className={`${tailwindClassnames.menuItem} rounded-t-xl`}
                  onClick={() => toggleDropdown(false)}
                >
                  {t("My Profile")}
                </NavLink>
                <NavLink
                  to="/transfer"
                  className={tailwindClassnames.menuItem}
                  onClick={() => toggleDropdown(false)}
                  data-testid="menu-get-akili-transfer"
                >
                  {t("Get Akili Transfer")}
                </NavLink>
                {/* The guide is hosted on the project site, outside this app, so
                    this is a plain anchor rather than a router link. A new tab
                    leaves the current page, and any running upload, untouched. */}
                <a
                  href={USER_GUIDE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={tailwindClassnames.menuItem}
                  onClick={() => toggleDropdown(false)}
                  data-testid="menu-user-guide"
                >
                  {t("User guide")}
                </a>
                <div className={tailwindClassnames.menuItem}>
                  <LanguageDropdown
                    onLanguageSelect={() => toggleDropdown(false)}
                  />
                </div>
                {/* A plain middle item: the rounding lives on the first and last
                    items, and this menu has no role="menu" or arrow-key navigation to
                    unpick. Header does not own the dialog — it asks the bus, so the
                    dialog stays reachable when this menu is not rendered at all. */}
                <button
                  type="button"
                  className={tailwindClassnames.menuItem}
                  onClick={() => {
                    toggleDropdown(false);
                    openReportProblem("menu");
                  }}
                  data-testid="menu-report-a-problem"
                >
                  {t("Report a problem")}
                </button>
                <div
                  className={`${tailwindClassnames.menuItem} rounded-b-xl`}
                  onClick={() => toggleDropdown(false)}
                >
                  <LogoutButton />
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
